import { fieldConfig } from "@autoform/zod";
import * as z from "zod";
import { GRADING_SETS } from "../common/enums";

// The flex module carries only kiosk-specific configuration. Partner-level fields
// (languages, legalDocs, paymentMethods, componentVersion) come from the shared
// `partner` module and colors/fonts from the shared `theme` module — both composed
// into the flex solution (solutions/flexSolution.ts).

// ---------------------------------------------------------------------------
// Home Screen
// ---------------------------------------------------------------------------

const homeScreenSchema = z.object({
	backgroundImage: z
		.string()
		.optional()
		.check(
			fieldConfig({
				fieldType: "url",
				description: "The URL of the background image.",
			}),
		)
		.describe("Home Background"),
	fontColor: z
		.string()
		.default("#1A1A1A")
		.check(fieldConfig({ fieldType: "color" }))
		.describe("Font Color"),
	subtitle: z.string().optional().describe("Subtitle"),
	title: z.string().optional().describe("Title"),
	locker: z.boolean().default(false).describe("Display Locker Swipe-Up"),
});

// ---------------------------------------------------------------------------
// Hope To See You Back Screen
// ---------------------------------------------------------------------------

const hopeToSeeYouBackSchema = z.object({
	displayRatingSection: z.boolean().default(true).describe("Display Rating Section"),
	sendGradingResults: z.boolean().default(false).describe("Send Grading Results / Offer Email"),
	header: z.string().optional().describe("Header Text"),
	body: z.string().optional().describe("Body Text"),
});

// ---------------------------------------------------------------------------
// Offer Screen
// ---------------------------------------------------------------------------

const offerScreenSchema = z.object({
	displayEstimatedPrice: z.boolean().default(true).describe("Display Estimated Price"),
	rejectOfferButton: z.boolean().default(false).describe("Reject Offer Button"),
	rejectOfferButtonAction: z
		.enum(["Exit", "Hope to see you back", "Trade in later"])
		.default("Exit")
		.check(fieldConfig({ fieldType: "singleselect" }))
		.describe("Reject Offer Button Action"),
	evaluationReport: z.boolean().default(true).describe("Evaluation Report"),
});

// ---------------------------------------------------------------------------
// Process Complete
// ---------------------------------------------------------------------------

const partnerSectionSchema = z.object({
	header: z.string().optional().describe("Custom Partner Header Text"),
	body: z.string().optional().describe("Custom Partner Text"),
});

const processCompleteSchema = z.object({
	header: z
		.enum(["Congratulations!", "Transaction completed!", "Grading completed!"])
		.default("Transaction completed!")
		.check(fieldConfig({ fieldType: "singleselect" }))
		.describe("Header Text"),
	partnerSection: partnerSectionSchema.prefault({}).describe("Partner Section"),
	transactionDetails: z.boolean().default(false).describe("Transaction Details"),
	rateDisplay: z
		.enum(["popup", "section", "hidden", "triggered"])
		.default("popup")
		.check(
			fieldConfig({
				fieldType: "singleselect",
				description:
					"Choose how the rate experience section will be displayed, as a section, in a popup or not at all.",
			}),
		)
		.describe("Rate Experience"),
	automaticOpen: z.boolean().default(false).describe("Automatic Open on Transaction Completion"),
	newGradingButton: z.boolean().default(true).describe("New Grading Button"),
	finaliseTransaction: z.boolean().default(true).describe("Finalise Transaction"),
	assessmentReport: z
		.enum(["swipeUp", "summary", "hidden"])
		.default("hidden")
		.check(fieldConfig({ fieldType: "singleselect" }))
		.describe("Assessment Report Type"),
	shouldStayInTheDrawer: z.boolean().default(false).describe("Device Should Be in the Drawer to Finalise"),
	disableTimer: z.boolean().default(false).describe("Disable Timer"),
});

// ---------------------------------------------------------------------------
// Companion App
// ---------------------------------------------------------------------------

const externalAppSchema = z.object({
	openUrl: z
		.string()
		.optional()
		.check(
			fieldConfig({
				fieldType: "url",
				description: "The url to the app if it is a web app",
			}),
		)
		.describe("Open URL"),
	downloadUrl: z
		.string()
		.optional()
		.check(
			fieldConfig({
				fieldType: "url",
				description: "The url to download the app",
			}),
		)
		.describe("Download URL"),
});

const companionAppSchema = z.object({
	none: z.boolean().default(false).describe("No Companion App"),
	// The webapp flow slug is always the partner code, so only the toggle is configurable.
	pandasWebapp: z.boolean().default(false).describe("Pandas Webapp"),
	externalApp: externalAppSchema.prefault({}).describe("External App"),
	shouldHaveCompletedCompanionApp: z.boolean().default(false).describe("Should Have Completed Companion App"),
	appName: z.string().default("Pandas").describe("App Name"),
});

// ---------------------------------------------------------------------------
// Instructions Steps
// ---------------------------------------------------------------------------

const instructionsStepsSchema = z.object({
	accessories: z.boolean().default(true).describe("Accessories"),
	hasCompletedCompanionApp: z.boolean().default(false).describe("Complete Companion App"),
	openCompanionApp: z.boolean().default(true).describe("Open Companion App"),
	operatingSystem: z.boolean().default(false).describe("Operating System"),
	frontCammeraScan: z.boolean().default(false).describe("Front Camera Scan"),
	displayImei: z.boolean().default(true).describe("Display IMEI"),
	submitImei: z.boolean().default(false).describe("Submit IMEI"),
});

// ---------------------------------------------------------------------------
// Claim Offer Steps
// ---------------------------------------------------------------------------

const claimOfferStepsSchema = z.object({
	evaluationReport: z.boolean().default(true).describe("Evaluation Report"),
	offer: z.boolean().default(true).describe("Offer Page"),
	openCompanionApp: z.boolean().default(false).describe("Open Companion App"),
	userInfo: z.boolean().default(true).describe("User Information"),
	transactionDetails: z.boolean().default(true).describe("Transaction Details"),
	signature: z.boolean().default(true).describe("Signature"),
	fmi: z.boolean().default(true).describe("FindMy Check"),
	accountsSignOut: z.boolean().default(false).describe("Sign Out of Accounts"),
	processComplete: z.boolean().default(true).describe("Process Complete"),
});

// ---------------------------------------------------------------------------
// Flex Schema
// ---------------------------------------------------------------------------

export const flexShape = {
	discrepancyFlow: z.boolean().default(false).describe("Discrepancy Flow"),
	manualFlip: z.boolean().default(false).describe("Manual Flip"),
	pregrade: z.boolean().default(true).describe("Perform Pregrade"),
	descriptiveButtons: z.boolean().default(false).describe("Descriptive Buttons"),
	homeScreen: homeScreenSchema.prefault({}).describe("Home Screen"),
	promoVideo: z.string().default("pandas-flex").describe("Promo Video Name"),
	drawerLock: z.boolean().default(true).describe("Lock Drawer"),
	displayEvaluationReport: z.boolean().default(true).describe("Evaluation Report"),
	hopeToSeeYouBackScreen: hopeToSeeYouBackSchema.prefault({}).describe("Hope to See You Back Screen"),
	offerScreen: offerScreenSchema.prefault({}).describe("Offer Screen"),
	confirmationType: z
		.enum(["Terms only", "Signature", "OTP"])
		.default("Terms only")
		.check(
			fieldConfig({
				fieldType: "singleselect",
				description: "Type of user confirmation",
			}),
		)
		.describe("Terms & Identity Confirmation"),
	otp: z.boolean().default(false).describe("OTP Verification"),
	processComplete: processCompleteSchema.prefault({}).describe("Process Complete"),
	companionApp: companionAppSchema.prefault({}).describe("Companion App"),
	instructionsSteps: instructionsStepsSchema.prefault({}).describe("Instructions Steps"),
	claimOfferSteps: claimOfferStepsSchema.prefault({}).describe("Complete Transaction Steps"),
	// The pricing app slug is always the partner code, so only the toggle is configurable.
	quickPricing: z.boolean().default(false).describe("Quick Pricing"),
	gradingSet: z
		.enum(GRADING_SETS)
		.default("grade")
		.check(fieldConfig({ fieldType: "singleselect" }))
		.describe("Grading Set"),
};

export const flexSchema = z.object(flexShape);

export type FlexConfig = z.infer<typeof flexSchema>;
